import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export default function Events() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);

  useEffect(() => {
    api.get("/events?past=false").then((r) => setUpcoming(r.data));
    api.get("/events?past=true").then((r) => setPast(r.data));
  }, []);

  return (
    <div data-testid="events-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 pb-12 sm:pt-24">
        <div className="eyebrow mb-3">Calendar</div>
        <h1 className="font-serif text-4xl sm:text-6xl text-brand-soft max-w-3xl leading-[1.05]">Upcoming events <span className="italic text-brand-maroon">for tails to remember.</span></h1>
        <p className="mt-5 text-base sm:text-lg text-brand-soft/70 max-w-2xl">Vaccinated dogs. Easy-going humans. We keep our gatherings small and warm.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-16">
        {upcoming.length === 0 ? (
          <div className="text-brand-soft/60 italic">No upcoming events yet — come back soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {upcoming.map((ev) => (
              <Link key={ev.id} to={`/events/${ev.id}`} className="group bg-white rounded-2xl border border-brand-maroon/10 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`event-card-${ev.id}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  {ev.cover_image && <img src={fileUrl(ev.cover_image)} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                </div>
                <div className="p-6 space-y-3">
                  <h3 className="font-serif text-2xl text-brand-soft leading-tight">{ev.title}</h3>
                  <div className="flex flex-wrap gap-3 text-xs text-brand-soft/70">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-brand-maroon" />{ev.date} {ev.time ? `· ${ev.time}` : ""}</span>
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brand-maroon" />{ev.location}</span>
                  </div>
                  <p className="text-sm text-brand-soft/70 leading-relaxed line-clamp-3">{ev.description}</p>
                  {ev.seats_note && (
                    <div className="text-xs font-medium text-brand-maroon">{ev.seats_note}</div>
                  )}
                  <div className="pt-2 inline-flex items-center gap-1.5 text-sm text-brand-maroon font-medium group-hover:gap-3 transition-all">
                    Book Now <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Past events */}
      <section className="bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
          <div className="eyebrow mb-3">Past pawties</div>
          <h2 className="font-serif text-3xl sm:text-5xl text-brand-soft mb-10">Memories we keep coming back to.</h2>
          {past.length === 0 ? (
            <div className="text-brand-soft/60 italic">No past events to show yet.</div>
          ) : (
            <div className="space-y-12">
              {past.map((ev) => (
                <div key={ev.id} className="space-y-5" data-testid={`past-event-${ev.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                    <div>
                      <h3 className="font-serif text-2xl text-brand-soft">{ev.title}</h3>
                      <div className="text-sm text-brand-soft/60">{ev.date} · {ev.location}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {(ev.gallery || []).length > 0 ? (
                      ev.gallery.map((g, i) => (
                        <div key={i} className="aspect-square overflow-hidden rounded-xl bg-white">
                          <img src={fileUrl(g)} alt={`${ev.title} ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                        </div>
                      ))
                    ) : ev.cover_image ? (
                      <div className="aspect-square overflow-hidden rounded-xl bg-white">
                        <img src={fileUrl(ev.cover_image)} alt={ev.title} className="w-full h-full object-cover" />
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
